import React from 'react';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Avatar, AvatarFallback } from 'components/ui/avatar';
import { StatusBadge } from 'components/StatusBadge';
import { formatDate, formatPrice, TRANSPORT_LABELS } from 'lib/format';
import { MapPin, Calendar, ArrowRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from 'lib/i18n';
import { TransportModeIcon } from 'components/TransportModeIcon';

export const TripCard = ({ trip, actionLabel, to }) => {
  const { t } = useLanguage();
  const link = to || `/trips/${trip.id}`;
  const remaining = (trip.capacity_kg || 0) - (trip.capacity_used_kg || 0);
  return (
    <Card className="transition-shadow duration-200 hover:shadow-md-custom hover:-translate-y-px overflow-hidden" data-testid={`trip-card-${trip.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent shrink-0">
              <TransportModeIcon mode={trip.transport_mode} size={16} className="text-foreground/70" />
            </div>
            <div>
              <div className="font-semibold text-sm">{TRANSPORT_LABELS[trip.transport_mode] || trip.transport_mode}</div>
              <div className="text-xs text-muted-foreground">{formatDate(trip.departure_date)}</div>
            </div>
          </div>
          <StatusBadge status={trip.status} />
        </div>

        <div className="flex items-center gap-2 text-sm mb-3">
          <MapPin size={14} className="text-primary shrink-0" />
          <span className="truncate font-medium">{trip.departure_city}, {trip.departure_country}</span>
          <ArrowRight size={14} className="text-muted-foreground shrink-0" />
          <span className="truncate font-medium">{trip.arrival_city}, {trip.arrival_country}</span>
        </div>

        <div className="grid grid-cols-3 gap-2 text-xs mb-3">
          <div className="rounded-md bg-secondary px-2 py-1.5">
            <div className="text-muted-foreground">{t('card.available')}</div>
            <div className="font-semibold">{remaining} kg</div>
          </div>
          <div className="rounded-md bg-secondary px-2 py-1.5">
            <div className="text-muted-foreground">{t('card.total')}</div>
            <div className="font-semibold">{trip.capacity_kg} kg</div>
          </div>
          <div className="rounded-md bg-secondary px-2 py-1.5">
            <div className="text-muted-foreground">{t('card.priceKg')}</div>
            <div className="font-semibold">
              {trip.price_per_kg ? formatPrice(trip.price_per_kg) : '—'}
              {trip.negotiable && <span className="font-normal text-muted-foreground"> ({t('card.negotiable')})</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t">
          <div className="flex items-center gap-2">
            <Avatar className="h-6 w-6">
              <AvatarFallback className="text-xs bg-secondary">
                {(trip.traveler_name || 'U').split(' ').map(n => n[0]).slice(0, 2).join('')}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs text-muted-foreground">{trip.traveler_name || t('card.traveler')}</span>
            {trip.traveler_rating > 0 && (
              <span className="flex items-center gap-0.5 text-xs">
                <Star size={10} className="fill-amber-500 text-amber-500" /> {trip.traveler_rating.toFixed(1)}
              </span>
            )}
          </div>
        </div>
        <Button asChild className="mt-4 min-h-11 w-full justify-between gap-3 rounded-lg px-4 text-sm font-semibold">
          <Link to={link} data-testid={`trip-card-open-button-${trip.id}`}>
            <span>{actionLabel || t('card.viewTrip')}</span>
            <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};
